<template>
  <div class="row">

    <div class="col-md-4">
      <div class="form-group" :class="{ 'marker-yellow': laneChanged }">
        <label for="sne-lane">Fila SNE</label>
        <select :value="lane" @change="setLane($event.target.value)"
                id="sne-lane" class="form-control">
          <option value="">Não definida</option>
          <option v-for="option in laneOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <code class="field-key">lane</code>
      </div>
    </div>

    <div class="col-md-8">
      <div class="params" :class="{ 'marker-yellow': paramsChanged }">
        <label for="sne-spider">Parâmetros</label>

        <select :value="spider" @change="selectSpider($event.target.value)"
                id="sne-spider" class="form-control">
          <option v-for="option in spiderOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>

        <div class="params-flags">
          <div v-for="field in paramFields" :key="field.key" class="param-flag">
            <BkpsCheckBox :value="isEnabled(field.key)" @input="setParam(field.key, $event)">
              <span class="param-label">{{ field.label }}</span>
            </BkpsCheckBox>
          </div>
        </div>

        <code class="field-key">spider_params.{{ spider }}</code>
      </div>
    </div>

    <div class="col-md-12">
      <div class="crlv" :class="{ 'marker-yellow': blockChanged }">
        <div class="crlv-head">
          <BkpsCheckBox :value="crlvBlocked" @input="setCrlvBlocked">
            <span class="crlv-label">Bloquear captura de CRLV</span>
          </BkpsCheckBox>

          <span class="crlv-status" :class="crlvBlocked ? 'crlv-off' : 'crlv-on'">
            {{ blockStatusLabel }}
          </span>
        </div>

        <p v-for="(line, index) in blockExplanation" :key="index" class="crlv-description">
          {{ line }}
        </p>

        <code class="field-key">{{ blockKey }}</code>
      </div>

      <div class="crlv" :class="{ 'marker-yellow': negotiatedChanged }">
        <div class="crlv-head">
          <span class="crlv-label">Valor negociado do CRLV</span>

          <span class="crlv-status" :class="negotiatedPrice ? 'crlv-on' : 'crlv-off'">
            {{ negotiatedStatusLabel }}
          </span>
        </div>

        <div class="crlv-price">
          <span>R$</span>
          <input :value="negotiatedPrice" @input="setNegotiatedPrice($event.target.value)"
                 type="text" inputmode="decimal" placeholder="tabela padrão" class="form-control">
          <span>por documento</span>
        </div>

        <p v-if="!negotiatedValid" class="crlv-description crlv-invalid">
          Informe um valor em reais maior que zero, por exemplo 4,50.
        </p>

        <p v-for="(line, index) in negotiatedExplanation" :key="'negotiated-' + index" class="crlv-description">
          {{ line }}
        </p>

        <p v-if="lastPriceChange" class="crlv-description crlv-audit">
          Alterado por <strong>{{ lastPriceChange.changed_by_email }}</strong> em {{ formatDate(lastPriceChange.changed_at) }}.
        </p>

        <div v-if="priceChanges.length" class="crlv-history">
          <a href="#" @click.prevent="showHistory = !showHistory">
            {{ showHistory ? 'Ocultar histórico' : `Ver histórico (${priceChanges.length})` }}
          </a>
          <ul v-if="showHistory">
            <li v-for="change in priceChanges" :key="change.id">
              {{ formatDate(change.changed_at) }} · <strong>{{ change.changed_by_email }}</strong> ·
              {{ priceLabel(change.previous_cents) }} → <strong>{{ priceLabel(change.new_cents) }}</strong>
            </li>
          </ul>
        </div>

        <code class="field-key">{{ negotiatedKey }}</code>
      </div>
    </div>

  </div>
</template>

<script>
  import BkpsCheckBox from '@/components/shares/specialTag/BkpsCheckBox'
  import {
    BLOCK_CRLV_EXPLANATION,
    BLOCK_CRLV_KEY,
    CRLV_NEGOTIATED_PRICE_EXPLANATION,
    CRLV_NEGOTIATED_PRICE_KEY,
    blockCrlvStatusLabel,
    crlvNegotiatedPrice,
    isCrlvBlocked,
    isCrlvNegotiatedPriceValid
  } from '@/utils/accountMetadataCatalog'

  const LANE_OPTIONS = [
    { value: 'pesada', label: 'Pesada' },
    { value: 'padrao', label: 'Padrão' },
    { value: 'express', label: 'Express' },
    { value: 'lab', label: 'Lab' }
  ]

  const SPIDER_OPTIONS = [
    { value: 'autos_sne_denatran40', label: 'SNE 40% (autos_sne_denatran40)' },
    { value: 'autos_sne_denatran20', label: 'SNE 20% (autos_sne_denatran20)' }
  ]

  const PARAM_FIELDS = [
    { key: 'get_paid_tickets', label: 'Incluir multas já pagas' },
    { key: 'get_boletos', label: 'Baixar boletos' },
    { key: 'get_other_files', label: 'Baixar outros documentos' }
  ]

  export default {
    components: { BkpsCheckBox },
    computed: {
      blockChanged() {
        return isCrlvBlocked(this.metadata) !== isCrlvBlocked(this.pristineMetadata)
      },
      blockExplanation() {
        return BLOCK_CRLV_EXPLANATION
      },
      blockKey() {
        return BLOCK_CRLV_KEY
      },
      blockStatusLabel() {
        return blockCrlvStatusLabel(this.crlvBlocked)
      },
      crlvBlocked() {
        return isCrlvBlocked(this.metadata)
      },
      lane() {
        return this.metadata.lane || ''
      },
      lastPriceChange() {
        return this.priceChanges[0] || null
      },
      pristineNegotiatedPrice() {
        return crlvNegotiatedPrice(this.pristineMetadata)
      },
      negotiatedChanged() {
        return crlvNegotiatedPrice(this.metadata) !== crlvNegotiatedPrice(this.pristineMetadata)
      },
      negotiatedExplanation() {
        return CRLV_NEGOTIATED_PRICE_EXPLANATION
      },
      negotiatedKey() {
        return CRLV_NEGOTIATED_PRICE_KEY
      },
      negotiatedPrice() {
        return crlvNegotiatedPrice(this.metadata)
      },
      negotiatedStatusLabel() {
        return this.negotiatedPrice ? `R$ ${this.negotiatedPrice} por documento` : 'Tabela padrão (R$ 10 / 8 / 6 / 5)'
      },
      negotiatedValid() {
        return isCrlvNegotiatedPriceValid(this.negotiatedPrice)
      },
      laneChanged() {
        return this.metadata.lane !== this.pristineMetadata.lane
      },
      laneOptions() {
        return LANE_OPTIONS
      },
      paramsChanged() {
        const pristine = (this.pristineMetadata.spider_params || {})[this.spider]

        return JSON.stringify(this.params) !== JSON.stringify(pristine || {})
      },
      paramFields() {
        return PARAM_FIELDS
      },
      params() {
        const all = this.metadata.spider_params || {}
        return all[this.spider] || {}
      },
      spiderOptions() {
        return SPIDER_OPTIONS
      }
    },
    created() {
      this.loadPriceChanges()

      const configured = Object.keys(this.metadata.spider_params || {})
      const known = configured.find(name => SPIDER_OPTIONS.some(option => option.value === name))

      if (known)
        this.spider = known
    },
    watch: {
      accountId() {
        this.loadPriceChanges()
      },
      pristineNegotiatedPrice() {
        this.loadPriceChanges()
      }
    },
    data() {
      return {
        priceChanges: [],
        showHistory: false,
        spider: SPIDER_OPTIONS[0].value
      }
    },
    methods: {
      formatDate(value) {
        return value ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : ''
      },
      loadPriceChanges() {
        if (!this.accountId)
          return

        this.axios.get('admin/crlv_captures/price_changes', { params: { account_id: this.accountId } })
          .then(response => this.priceChanges = (response && response.data) || [])
          .catch(() => this.priceChanges = [])
      },
      priceLabel(cents) {
        return cents ? (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'tabela padrão'
      },
      isEnabled(key) {
        return !!this.params[key]
      },
      selectSpider(value) {
        this.spider = value
      },
      setCrlvBlocked(value) {
        this.$set(this.metadata, BLOCK_CRLV_KEY, value)
        this.$emit('changed')
      },
      setNegotiatedPrice(value) {
        const text = String(value || '').trim()

        if (text)
          this.$set(this.metadata, CRLV_NEGOTIATED_PRICE_KEY, text.replace(',', '.'))
        else
          this.$delete(this.metadata, CRLV_NEGOTIATED_PRICE_KEY)

        this.$emit('changed')
      },
      setLane(value) {
        if (value)
          this.$set(this.metadata, 'lane', value)
        else
          this.$delete(this.metadata, 'lane')

        this.$emit('changed')
      },
      setParam(key, value) {
        if (!this.metadata.spider_params)
          this.$set(this.metadata, 'spider_params', {})

        if (!this.metadata.spider_params[this.spider])
          this.$set(this.metadata.spider_params, this.spider, {})

        this.$set(this.metadata.spider_params[this.spider], key, value)
        this.$emit('changed')
      }
    },
    name: 'SneSettings',
    props: {
      accountId: { default: null, type: [Number, String] },
      metadata: { required: true, type: Object },
      pristineMetadata: { default: () => ({}), type: Object }
    }
  }
</script>

<style scoped>
  label {
    font-size: 12px;
    color: var(--secondary-text-color);
  }

  .marker-yellow {
    border-left: solid 5px #FFC866;
    border-radius: 5px 0 0 5px;
    padding-left: 12px;
  }

  .field-key {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: var(--text-muted);
  }

  .crlv {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--border-color);
  }

  .crlv.marker-yellow {
    border-top: none;
    padding-top: 0;
  }

  .crlv-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }

  .crlv-label {
    display: inline-flex;
    align-items: center;
    min-height: 16px;
    margin-left: 8px;
    line-height: 16px;
    font-size: 14px;
    font-weight: 600;
    color: var(--primary-text-color);
  }

  .crlv-status {
    font-size: 12px;
    font-weight: 600;
  }

  .crlv-on {
    color: #15ce9a;
  }

  .crlv-off {
    color: var(--secondary-text-color);
  }

  .crlv-price {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
    font-size: 12px;
    color: var(--secondary-text-color);
  }

  .crlv-price input {
    width: 130px;
  }

  .crlv-audit {
    margin-top: 6px;
  }

  .crlv-history {
    font-size: 12px;
    margin: 4px 0 6px;
  }

  .crlv-history ul {
    margin: 6px 0 0;
    padding-left: 14px;
    border-left: 1px solid #dee2e6;
    list-style: none;
    color: var(--secondary-text-color);
  }

  .crlv-history li {
    margin-bottom: 3px;
  }

  .crlv-invalid {
    color: #c0392b;
  }

  .crlv-description {
    margin: 6px 0 0 40px;
    max-width: 720px;
    font-size: 12px;
    line-height: 16px;
    color: var(--secondary-text-color);
  }

  .crlv .field-key {
    margin-left: 40px;
  }

  .params-flags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 32px;
    margin-top: 12px;
  }

  .param-label {
    display: inline-flex;
    align-items: center;
    min-height: 16px;
    margin-left: 8px;
    line-height: 16px;
    font-size: 14px;
    color: var(--primary-text-color);
  }
</style>
